"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { KeyStream } from "@/components/tools/ciphers-polygraphic/key-stream";
import { KeyedWorking } from "@/components/tools/ciphers-polygraphic/keyed-working";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  ALPHABET,
  foreignLetters,
  KEEP_ALL_TEXT,
  letterIndex,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  type AutokeySource,
  autokeyTransform,
  vigenereSquare,
} from "@/lib/tool-kit/ciphers-polygraphic/autokey";
import {
  describeCalc,
  formatCipherOutput,
} from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";

const STEP_LIMIT = 20;
const STREAM_LIMIT = 26;
const LETTERS = [...ALPHABET];

/** English strings. A translated content file overrides these through `ui`. */
export const AUTOKEY_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "Attack at dawn",
  primer: "Primer",
  primerSample: "QUEENLY",
  primerHint:
    "A short keyword that only starts the key. After it, the key carries on with the message itself, so it never repeats.",
  primerMissing: "Enter a primer with at least one letter to see the result.",
  source: "What continues the key",
  sourcePlain: "The plain text (classic autokey)",
  sourceCipher: "The cipher text",
  sourceHint:
    "Plain-text autokey is the classical cipher and the one puzzle sites mean by “autokey”. The cipher-text form is the other version found in textbooks; both sides must pick the same one.",
  group: "Cipher text in groups of five (letters only, in capitals)",
  keep: "Keep in the result",
  keepCase: "Lower case",
  keepSpaces: "Spaces",
  keepPunctuation: "Punctuation and numbers",
  foreign:
    "one: {count} letter outside A–Z was left unchanged: {letters}.|other: {count} letters outside A–Z were left unchanged: {letters}.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  working: "Working",
  numbering:
    "Letters are numbered A = 0, B = 1 … Z = 25, and every sum wraps around at 26.",
  ruleEncode: "Encoding: cipher letter = plain letter + key letter.",
  ruleDecode: "Decoding: plain letter = cipher letter − key letter.",
  ruleKeyPlain:
    "The key is the primer followed by the plain text, so while decoding every letter you recover joins the key.",
  ruleKeyCipher:
    "The key is the primer followed by the cipher text, so the whole key can be read straight from the cipher text.",
  streamTitle:
    "one: The key, letter by letter — the first letter|other: The key, letter by letter — the first {count} letters",
  streamPlain: "Plain",
  streamKey: "Key",
  streamCipher: "Cipher",
  streamNotePlain:
    "one: The first key letter (shaded) is the primer; after that each key letter is the plain-text letter one place back.|other: The first {count} key letters (shaded) are the primer; after that each key letter is the plain-text letter {count} places back.",
  streamNoteCipher:
    "one: The first key letter (shaded) is the primer; after that each key letter is the cipher-text letter one place back.|other: The first {count} key letters (shaded) are the primer; after that each key letter is the cipher-text letter {count} places back.",
  squareLabel: "Vigenère square",
  squareHint:
    "Row = key letter, column = plain letter, cell = cipher letter. Select a letter below to light it up: its row and column turn blue and the cell where they meet turns amber.",
  plainLetter: "Plain letter",
  cipherLetter: "Cipher letter",
  keyLetter: "Key letter",
  arithmetic: "Arithmetic",
  showLetter: "Show {letter} in the square",
  stepLimit:
    "one: Showing the first letter.|other: Showing the first {count} letters.",
} as const;

export default function AutokeyCipherWidget() {
  const strings = useToolUi(AUTOKEY_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [primer, setPrimer] = useState<string>(strings.primerSample);
  const [source, setSource] = useState<AutokeySource>("plain");
  const [options, setOptions] = useState(KEEP_ALL_TEXT);
  const [group, setGroup] = useState(false);

  const result = autokeyTransform(input, primer, source, mode);
  const encoding = mode === "encode";
  const output = formatCipherOutput(result.output, options, group && encoding);
  const foreign = foreignLetters(input);
  const shown = result.steps.slice(0, STEP_LIMIT);
  const streamed = result.steps.slice(0, STREAM_LIMIT);
  const primerLength = Math.min(result.primer.length, streamed.length);

  const keyControls = (
    <div className="space-y-4">
      <TextField
        hint={strings.primerHint}
        id="ak-primer"
        label={strings.primer}
        mono
        onChange={setPrimer}
        value={primer}
      />
      <PillGroup
        label={strings.source}
        name="ak-source"
        onChange={setSource}
        options={[
          { value: "plain", label: strings.sourcePlain },
          { value: "cipher", label: strings.sourceCipher },
        ]}
        value={source}
      />
      <p className={HINT}>{strings.sourceHint}</p>
      {encoding ? (
        <Checkbox checked={group} label={strings.group} onChange={setGroup} />
      ) : null}
    </div>
  );

  const working =
    result.primer === "" ? (
      <Notice>{strings.primerMissing}</Notice>
    ) : (
      <>
        <KeyStream
          note={tPlural(
            source === "plain"
              ? strings.streamNotePlain
              : strings.streamNoteCipher,
            locale,
            result.primer.length,
            { count: fmt(result.primer.length) }
          )}
          rows={[
            {
              label: strings.streamPlain,
              letters: streamed.map((step) => step.plain),
            },
            {
              label: strings.streamKey,
              letters: streamed.map((step) => step.key),
            },
            {
              label: strings.streamCipher,
              letters: streamed.map((step) => step.cipher),
            },
          ]}
          shaded={primerLength}
          title={tPlural(strings.streamTitle, locale, streamed.length, {
            count: fmt(streamed.length),
          })}
        />
        <KeyedWorking
          head={[
            encoding ? strings.plainLetter : strings.cipherLetter,
            strings.keyLetter,
            strings.arithmetic,
            encoding ? strings.cipherLetter : strings.plainLetter,
          ]}
          limitNote={
            result.steps.length > STEP_LIMIT
              ? tPlural(strings.stepLimit, locale, STEP_LIMIT, {
                  count: fmt(STEP_LIMIT),
                })
              : undefined
          }
          marks={shown.map((step) => ({
            row: letterIndex(step.key),
            column: letterIndex(step.plain),
          }))}
          rows={shown.map((step) => [
            encoding ? step.plain : step.cipher,
            step.key,
            describeCalc(step.calc),
            encoding ? step.cipher : step.plain,
          ])}
          rules={[
            strings.numbering,
            encoding ? strings.ruleEncode : strings.ruleDecode,
            source === "plain" ? strings.ruleKeyPlain : strings.ruleKeyCipher,
          ]}
          selectLabel={strings.showLetter}
          square={{
            cells: vigenereSquare(),
            columns: LETTERS.length,
            label: strings.squareLabel,
            hint: strings.squareHint,
            rowHeaders: LETTERS,
            columnHeaders: LETTERS,
          }}
        />
      </>
    );

  return (
    <CipherWorkbench
      idPrefix="ak"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: encoding ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: encoding ? strings.cipherText : strings.decodedText,
        outputPlaceholder: strings.outputPlaceholder,
        textOptions: strings.keep,
        keepCase: strings.keepCase,
        keepSpaces: strings.keepSpaces,
        keepPunctuation: strings.keepPunctuation,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={
        foreign.count > 0 ? (
          <Notice>
            {tPlural(strings.foreign, locale, foreign.count, {
              count: fmt(foreign.count),
              letters: foreign.letters.join(" "),
            })}
          </Notice>
        ) : null
      }
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      textOptions={
        group && encoding
          ? undefined
          : {
              value: options,
              onChange: setOptions,
              show: ["keepCase", "keepSpaces", "keepPunctuation"],
            }
      }
      working={working}
    />
  );
}
